/**
 * SecretsCard Component
 *
 * Displays a list of secrets for an organization with permission-gated actions.
 * Users can view, edit, or create secrets based on their permissions.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    useDeleteSecret,
    useDisableSecret,
    useEnableSecret,
    useDisconnectCredentialUsages,
} from '@/lib/hooks/useSecrets';
import {
    InfiniteScrollListEmptyState,
    InfiniteScrollListLoadingState,
} from '@/components/ui/infinite-scroll-list-state';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { SecretModal } from './SecretModal';
import { SecretActionButtons } from './SecretActionButtons';
import { CredentialCardView } from './CredentialCardView';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { secretsApi } from '@/lib/pillarApi';
import { createComponentLogger, type ComponentLogger } from '@/lib/logging/frontend-logger';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import { parseCredentialDeleteConflictUsages } from '@/lib/secrets/credentialDeleteConflict';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';
import { runInBackground } from '@/lib/utils';
import type {
    CreateSecretResponse,
    CredentialUsageListItem,
    CredentialUsageReference,
    ListSecretsResponseCapabilities,
    SecretListItem,
    SupportedCredentialSystemId,
} from '@anchorbase/pillar-sdk';
import {
    GenericThirdPartySystemId,
    IpgSystemId,
    IpwSystemId,
    ThirdPartySystemId,
    formatThirdPartySystemName,
} from '@anchorbase/pillar-sdk';
import { Plus } from 'lucide-react';
import { CredentialDeleteDialog } from './CredentialDeleteDialog';
import { CredentialDisconnectDialog } from './CredentialDisconnectDialog';

export type CredentialTargetFamily = 'all' | 'ipg' | 'ipw' | 'generic_website';

export const SecretsCardOpaqueCursorSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1);

export interface GenericWebsiteCredentialOption {
    readonly value: string;
    readonly label: string;
}

interface SecretsCardProps {
    organizationId?: number;
    organizationUuid?: string;
    organizationName: string;
    allowSubsidiariesToggle?: boolean;
    scrollToCredentialUuid?: string;
    scrollToSection?: boolean;
    credentialTargetFamily?: CredentialTargetFamily;
    title?: string;
    enabledIpgSystemIds?: readonly IpgSystemId[];
    enabledIpwSystemIds?: readonly IpwSystemId[];
    genericWebsiteCredentialOptions?: readonly GenericWebsiteCredentialOption[];
    defaultCollapsed?: boolean;
}

export function buildCredentialCardElementId(credentialUuid: string): string {
    return `credential-card-${credentialUuid}`;
}

function getCredentialCardTitle(
    credentialTargetFamily: CredentialTargetFamily,
    title: string | undefined
): string {
    if (title !== undefined) {
        return title;
    }
    switch (credentialTargetFamily) {
        case 'all':
            return 'Credentials';
        case 'ipg':
            return 'Integrated Payments Gateway Credentials';
        case 'ipw':
            return 'Integrated Payments Website Credentials';
        case 'generic_website':
            return 'Generic Website Credentials';
    }
}

function getCredentialSystemSelectLabel(
    credentialTargetFamily: CredentialTargetFamily
): string | undefined {
    switch (credentialTargetFamily) {
        case 'ipg':
            return 'System ID';
        case 'ipw':
            return 'System ID';
        case 'generic_website':
            return 'Generic Website';
        case 'all':
            return undefined;
    }
}

function getAvailableCredentialSystemIds(
    credentialTargetFamily: CredentialTargetFamily,
    enabledIpgSystemIds: readonly IpgSystemId[],
    enabledIpwSystemIds: readonly IpwSystemId[]
): readonly SupportedCredentialSystemId[] | undefined {
    switch (credentialTargetFamily) {
        case 'all':
            return undefined;
        case 'ipg':
            return enabledIpgSystemIds;
        case 'ipw':
            return enabledIpwSystemIds;
        case 'generic_website':
            return [GenericThirdPartySystemId];
    }
}

function buildSecretsCardOrganizationLogContext(
    organizationId?: number,
    organizationUuid?: string
): Record<string, string | number> {
    const logContext: Record<string, string | number> = {};
    if (organizationId !== undefined) {
        logContext.organizationId = organizationId;
    }
    if (organizationUuid !== undefined) {
        logContext.organizationUuid = organizationUuid;
    }
    return logContext;
}

export function SecretsCard({
    organizationId,
    organizationUuid,
    organizationName,
    allowSubsidiariesToggle,
    scrollToCredentialUuid,
    scrollToSection = false,
    credentialTargetFamily = 'all',
    title,
    enabledIpgSystemIds = [],
    enabledIpwSystemIds = [],
    genericWebsiteCredentialOptions = [],
    defaultCollapsed = false,
}: SecretsCardProps) {
    const log: ComponentLogger = createComponentLogger('SecretsCard');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSecret, setSelectedSecret] = useState<SecretListItem | undefined>();
    const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [secretToDelete, setSecretToDelete] = useState<SecretListItem | null>(null);
    const [selectedDeleteUsageKeys, setSelectedDeleteUsageKeys] = useState<string[]>([]);
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const [secretToDisconnect, setSecretToDisconnect] = useState<SecretListItem | null>(null);
    const [selectedUsageKeys, setSelectedUsageKeys] = useState<string[]>([]);
    const [includeDescendants, setIncludeDescendants] = useState<boolean>(false);
    const [deleteConflictUsages, setDeleteConflictUsages] = useState<CredentialUsageListItem[] | null>(
        null
    );
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(!defaultCollapsed);
    const [listCapabilities, setListCapabilities] =
        useState<ListSecretsResponseCapabilities | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const queryClient = useQueryClient();
    const hasUnsavedChanges: boolean = modalOpen && mode !== 'view';
    useUnsavedChangesRegistration(hasUnsavedChanges);

    const canShowDeleted: boolean = listCapabilities?.can_show_deleted === true;
    const canCreateAllSecretsWildcard: boolean = listCapabilities?.can_create === true;

    const availableCredentialSystemIds: readonly SupportedCredentialSystemId[] | undefined =
        getAvailableCredentialSystemIds(
            credentialTargetFamily,
            enabledIpgSystemIds,
            enabledIpwSystemIds
        );
    const listQueryKeyPrefix: ReadonlyArray<unknown> = useMemo(
        () =>
            buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'secrets',
                'organization',
                organizationUuid ?? organizationId ?? 'no-organization',
                credentialTargetFamily,
                `descendants=${includeDescendants}`,
                `showDeleted=${canShowDeleted && showDeleted}`,
                `systems=${(availableCredentialSystemIds ?? []).join(',')}`,
            ]),
        [
            activeRoleAssignmentId,
            availableCredentialSystemIds,
            canShowDeleted,
            credentialTargetFamily,
            includeDescendants,
            organizationId,
            organizationUuid,
            showDeleted,
        ]
    );
    const list = useInfiniteScrollList<SecretListItem, string, ReadonlyArray<unknown>>(
        {
            queryKeyPrefix: listQueryKeyPrefix,
            pageSize: 50,
            enabled:
                isExpanded &&
                organizationUuid !== undefined &&
                (availableCredentialSystemIds === undefined ||
                    availableCredentialSystemIds.length > 0),
            parseCursorParam: (pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = SecretsCardOpaqueCursorSchema.safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            },
            fetchPage: async ({ limit, cursor }) => {
                if (organizationUuid === undefined) {
                    throw new Error('Organization UUID is required');
                }
                const response = await secretsApi.listSecrets(
                    { organization_uuid: organizationUuid },
                    {
                        include_descendants: includeDescendants,
                        show_deleted: canShowDeleted && showDeleted,
                        system_ids:
                            availableCredentialSystemIds === undefined
                                ? undefined
                                : [...availableCredentialSystemIds],
                        exclude_policy_linked: true,
                        limit,
                        cursor,
                    }
                );
                setListCapabilities(response.capabilities);
                return {
                    items: response.secrets,
                    pagination: response.pagination ?? { has_more: false },
                };
            },
            rootMargin: '1200px 0px',
            observerRootRef: scrollContainerRef,
        }
    );
    const visibleSecrets: readonly SecretListItem[] = list.items;
    const cardTitle: string = getCredentialCardTitle(credentialTargetFamily, title);
    const canCreateCredentialInCard: boolean =
        canCreateAllSecretsWildcard === true &&
        (availableCredentialSystemIds === undefined || availableCredentialSystemIds.length > 0) &&
        (credentialTargetFamily !== 'generic_website' ||
            genericWebsiteCredentialOptions.length > 0);
    const createCredentialDisabledReason: string =
        credentialTargetFamily === 'generic_website'
            ? 'Add an unmanaged allowed website before creating generic website credentials.'
            : 'Enable at least one target before creating credentials.';
    const visibleSecretIdsKey: string = visibleSecrets
        .map((secret) => secret.credential_uuid)
        .join(',');

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        if (scrollToCredentialUuid !== undefined) {
            const targetElement = document.getElementById(
                buildCredentialCardElementId(scrollToCredentialUuid)
            );
            if (targetElement instanceof HTMLElement) {
                targetElement.scrollIntoView({ block: 'center', behavior: 'auto' });
                return;
            }
        }

        if (scrollToSection) {
            cardRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
    }, [
        isExpanded,
        scrollToCredentialUuid,
        scrollToSection,
        visibleSecretIdsKey,
    ]);
    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();

    if (!isExpanded) {
        return (
            <Card className="mb-6" ref={cardRef}>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{cardTitle}</CardTitle>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setIsExpanded(true);
                        }}
                    >
                        Expand
                    </Button>
                </CardHeader>
            </Card>
        );
    }

    const handleCreate = () => {
        setSelectedSecret(undefined);
        setMode('create');
        setModalOpen(true);
    };

    const handleView = (secret: SecretListItem) => {
        setSelectedSecret(secret);
        setMode('view');
        setModalOpen(true);
    };

    const handleEdit = (secret: SecretListItem) => {
        setSelectedSecret(secret);
        setMode('edit');
        setModalOpen(true);
    };

    const handleDeleteClick = (secret: SecretListItem) => {
        setSecretToDelete(secret);
        setDeleteConflictUsages(null);
        setSelectedDeleteUsageKeys([]);
        setDeleteConfirmOpen(true);
    };

    const deleteCredentialUsages: CredentialUsageListItem[] =
        deleteConflictUsages ?? secretToDelete?.usage_data.usages ?? [];

    const handleDisconnectClick = (secret: SecretListItem) => {
        setSecretToDisconnect(secret);
        setSelectedUsageKeys([]);
        setDisconnectDialogOpen(true);
    };

    const handleToggleUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ) => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };

    const handleConfirmDisconnect = () => {
        if (secretToDisconnect === null) {
            log.error(
                'Disconnect confirmation clicked with no secret selected',
                undefined,
                buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
            );
            return;
        }
        const availableUsages: CredentialUsageListItem[] =
            secretToDisconnect?.usage_data.usages ?? [];
        const selectedUsages: CredentialUsageReference[] = availableUsages
            .filter((usage) => selectedUsageKeys.includes(buildCredentialUsageSelectionKey(usage)))
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        const disconnectVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secretToDisconnect.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                      usages: selectedUsages,
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secretToDisconnect.credential_uuid,
                        },
                        organizationId,
                        usages: selectedUsages,
                    }
                  : undefined;
        if (disconnectVariables === undefined) {
            log.error('Disconnect requested without an organization reference', undefined, {
                credentialUuid: secretToDisconnect.credential_uuid,
            });
            return;
        }
        disconnectMutation.mutate(
            disconnectVariables,
            {
                onSuccess: () => {
                    setDisconnectDialogOpen(false);
                    setSecretToDisconnect(null);
                    setSelectedUsageKeys([]);
                },
                onError: (disconnectError: unknown) => {
                    log.error('Failed to disconnect credential usages', disconnectError, {
                        ...buildSecretsCardOrganizationLogContext(organizationId, organizationUuid),
                        credentialUuid: secretToDisconnect.credential_uuid,
                    });
                },
            }
        );
    };

    const handleDisable = (secret: SecretListItem) => {
        const disableVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secret.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secret.credential_uuid,
                        },
                        organizationId,
                    }
                  : undefined;
        if (disableVariables === undefined) {
            log.error('Disable requested without an organization reference', undefined, {
                credentialUuid: secret.credential_uuid,
            });
            return;
        }
        disableMutation.mutate(disableVariables);
    };

    const handleEnable = (secret: SecretListItem) => {
        const enableVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secret.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secret.credential_uuid,
                        },
                        organizationId,
                    }
                  : undefined;
        if (enableVariables === undefined) {
            log.error('Enable requested without an organization reference', undefined, {
                credentialUuid: secret.credential_uuid,
            });
            return;
        }
        enableMutation.mutate(enableVariables);
    };

    const handleConfirmDelete = () => {
        if (secretToDelete) {
            const deleteVariables =
                organizationUuid !== undefined
                    ? {
                          secret: {
                              credential_uuid: secretToDelete.credential_uuid,
                          },
                          organization: {
                              organization_uuid: organizationUuid,
                          },
                      }
                    : organizationId !== undefined
                      ? {
                            secret: {
                                credential_uuid: secretToDelete.credential_uuid,
                            },
                            organizationId,
                        }
                      : undefined;
            if (deleteVariables === undefined) {
                log.error('Delete requested without an organization reference', undefined, {
                    credentialUuid: secretToDelete.credential_uuid,
                });
                return;
            }
            deleteMutation.mutate(
                deleteVariables,
                {
                    onSuccess: () => {
                        setDeleteConfirmOpen(false);
                        setSecretToDelete(null);
                        setDeleteConflictUsages(null);
                    },
                    onError: (deleteError: unknown) => {
                        const conflictUsages: CredentialUsageListItem[] | null =
                            parseCredentialDeleteConflictUsages(deleteError);
                        if (conflictUsages !== null) {
                            setDeleteConflictUsages(conflictUsages);
                        }
                        log.error('Failed to delete secret', deleteError, {
                            ...buildSecretsCardOrganizationLogContext(
                                organizationId,
                                organizationUuid
                            ),
                            credentialUuid: secretToDelete.credential_uuid,
                        });
                    },
                }
            );
        } else {
            log.error(
                'Delete confirmation clicked with no secret selected',
                undefined,
                buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
            );
        }
    };

    const handleToggleDeleteUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedDeleteUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedDeleteUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };

    const handleToggleDeleteSelectAll = (checked: boolean): void => {
        if (!checked) {
            setSelectedDeleteUsageKeys([]);
            return;
        }
        setSelectedDeleteUsageKeys(
            deleteCredentialUsages.map((usage) => buildCredentialUsageSelectionKey(usage))
        );
    };

    const handleSecretSaved = (savedSecret: CreateSecretResponse): void => {
        if (savedSecret.organization_uuid !== organizationUuid) {
            return;
        }
        runInBackground(
            queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix }),
            (err: unknown) => {
                log.error('Failed to refresh credential card list after save', err, {
                    ...buildSecretsCardOrganizationLogContext(
                        organizationId,
                        organizationUuid
                    ),
                    credentialUuid: savedSecret.credential_uuid,
                });
            }
        );
    };

    const handleRemoveDeleteUsages = (): void => {
        if (secretToDelete === null) {
            return;
        }
        const selectedUsages: CredentialUsageReference[] = deleteCredentialUsages
            .filter((usage) =>
                selectedDeleteUsageKeys.includes(buildCredentialUsageSelectionKey(usage))
            )
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        const disconnectVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secretToDelete.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                      usages: selectedUsages,
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secretToDelete.credential_uuid,
                        },
                        organizationId,
                        usages: selectedUsages,
                    }
                  : undefined;
        if (disconnectVariables === undefined) {
            log.error(
                'Remove-from-delete requested without an organization reference',
                undefined,
                {
                    credentialUuid: secretToDelete.credential_uuid,
                }
            );
            return;
        }
        disconnectMutation.mutate(disconnectVariables, {
            onSuccess: () => {
                setSelectedDeleteUsageKeys([]);
                setDeleteConflictUsages(null);
            },
            onError: (disconnectError: unknown) => {
                log.error(
                    'Failed to remove credential usages from delete dialog',
                    disconnectError,
                    {
                        ...buildSecretsCardOrganizationLogContext(
                            organizationId,
                            organizationUuid
                        ),
                        credentialUuid: secretToDelete.credential_uuid,
                    }
                );
            },
        });
    };

    const getSystemName = (systemId: ThirdPartySystemId): string => {
        try {
            return formatThirdPartySystemName(systemId);
        } catch {
            return `System ${systemId}`;
        }
    };

    if (list.isLoading) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>{cardTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">Loading secrets...</p>
                </CardContent>
            </Card>
        );
    }

    if (list.error !== null) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>{cardTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>
                            {list.error.message}
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-6" ref={cardRef}>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{cardTitle}</CardTitle>
                    <div className="flex items-center gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setIsExpanded(false);
                            }}
                        >
                            Collapse
                        </Button>
                        {allowSubsidiariesToggle === true ? (
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={includeDescendants}
                                    onCheckedChange={(checked: boolean) => {
                                        setIncludeDescendants(checked === true);
                                    }}
                                />
                                <span>Show subsidiaries</span>
                            </label>
                        ) : null}
                        {canShowDeleted ? (
                            <div className="flex items-center gap-2">
                                <Label htmlFor="credentials-show-deleted" className="cursor-pointer">
                                    Show deleted
                                </Label>
                                <Checkbox
                                    id="credentials-show-deleted"
                                    checked={showDeleted}
                                    onCheckedChange={(checked) => {
                                        setShowDeleted(checked === true);
                                    }}
                                />
                            </div>
                        ) : null}
                        {canCreateAllSecretsWildcard === true ? (
                            <span
                                title={
                                    canCreateCredentialInCard
                                        ? undefined
                                        : createCredentialDisabledReason
                                }
                            >
                                <Button
                                    onClick={handleCreate}
                                    size="sm"
                                    disabled={!canCreateCredentialInCard}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Credentials
                                </Button>
                            </span>
                        ) : null}
                    </div>
                </CardHeader>
                <CardContent>
                    {deleteMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {deleteMutation.error instanceof Error
                                    ? deleteMutation.error.message
                                    : 'Failed to delete secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disableMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {disableMutation.error instanceof Error
                                    ? disableMutation.error.message
                                    : 'Failed to disable secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {enableMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {enableMutation.error instanceof Error
                                    ? enableMutation.error.message
                                    : 'Failed to enable secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disconnectMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {disconnectMutation.error instanceof Error
                                    ? disconnectMutation.error.message
                                    : 'Failed to disconnect credential usages'}
                            </AlertDescription>
                        </Alert>
                    )}

                    {visibleSecrets.length === 0 ? (
                        <InfiniteScrollListEmptyState label="No secrets configured for this organization." />
                    ) : (
                        <div
                            ref={scrollContainerRef}
                            className="max-h-[42rem] space-y-3 overflow-y-auto pr-1"
                        >
                            {visibleSecrets.map((secret) => (
                                <CredentialCardView
                                    key={secret.credential_uuid}
                                    id={buildCredentialCardElementId(secret.credential_uuid)}
                                    className={
                                        secret.credential_uuid === scrollToCredentialUuid
                                            ? 'bg-red-50'
                                            : undefined
                                    }
                                    credential={secret}
                                    showPublicFields={true}
                                    actions={
                                        <SecretActionButtons
                                            secret={secret}
                                            onView={() => handleView(secret)}
                                            onEdit={() => handleEdit(secret)}
                                            onDisable={() => handleDisable(secret)}
                                            onEnable={() => handleEnable(secret)}
                                            onDelete={() => handleDeleteClick(secret)}
                                            onDisconnect={() => handleDisconnectClick(secret)}
                                        />
                                    }
                                />
                            ))}
                            <div
                                ref={list.loadMoreSentinelRef}
                                className="h-8"
                            />
                            {list.isFetching ? (
                                <InfiniteScrollListLoadingState label="Loading secrets..." />
                            ) : null}
                        </div>
                    )}
                </CardContent>
            </Card>

            <SecretModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                organizationId={organizationId}
                organizationUuid={organizationUuid}
                organizationName={organizationName}
                secret={selectedSecret}
                mode={mode}
                credentialTargetFamily={credentialTargetFamily}
                credentialSystemSelectLabel={getCredentialSystemSelectLabel(
                    credentialTargetFamily
                )}
                availableCredentialSystemIds={availableCredentialSystemIds}
                genericWebsiteOptions={genericWebsiteCredentialOptions}
                onSecretSaved={handleSecretSaved}
            />

            <CredentialDeleteDialog
                open={deleteConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDeleteConfirmOpen(isOpen);
                    if (!isOpen) {
                        setSecretToDelete(null);
                        setDeleteConflictUsages(null);
                        setSelectedDeleteUsageKeys([]);
                    }
                }}
                credentialLabel={
                    secretToDelete === null
                        ? ''
                        : `${getSystemName(secretToDelete.system_id)} (${secretToDelete.account_id})`
                }
                usages={deleteCredentialUsages}
                isLoadingUsages={false}
                usageLoadError={null}
                selectedUsageKeys={selectedDeleteUsageKeys}
                onToggleUsageSelection={handleToggleDeleteUsageSelection}
                onToggleSelectAll={handleToggleDeleteSelectAll}
                onConfirm={handleConfirmDelete}
                onRemove={handleRemoveDeleteUsages}
                isDeleting={deleteMutation.isPending}
                isRemoving={disconnectMutation.isPending}
            />

            <CredentialDisconnectDialog
                open={disconnectDialogOpen}
                onOpenChange={(isOpen) => {
                    setDisconnectDialogOpen(isOpen);
                    if (!isOpen) {
                        setSecretToDisconnect(null);
                        setSelectedUsageKeys([]);
                    }
                }}
                usages={secretToDisconnect?.usage_data.usages ?? []}
                isLoadingUsages={false}
                usageLoadError={null}
                selectedUsageKeys={selectedUsageKeys}
                onToggleUsageSelection={handleToggleUsageSelection}
                onConfirm={handleConfirmDisconnect}
                isDisconnecting={disconnectMutation.isPending}
            />
        </>
    );
}
